import { Grid, Card, CardHeader, CardContent, Button, Table, TableHead, TableRow, TableCell, TableBody, Chip } from '@mui/material';
import { Autorenew, Verified, PendingActions, Block, Description } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../../components/PageHeader';
import { StatCard } from '../../../components/StatCard';
import { MiniBarChart } from '../../../components/MiniBarChart';
import { EmptyState } from '../../../components/EmptyState';
import { vendorDashboardApi } from '../../../api/vendorOnboarding.api';
import { useAuth } from '../../../hooks/useAuth';

const n = (x = 0) => Number(x).toLocaleString('en-IN');

function ChartCard({ title, data }: { title: string; data: Array<{ label: string; value: number }> }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader title={title} titleTypographyProps={{ variant: 'subtitle1' }} />
      <CardContent>{data?.length ? <MiniBarChart data={data.map((d) => ({ ...d, color: '#1e40af' }))} /> : <EmptyState title="No data yet" dense />}</CardContent>
    </Card>
  );
}

export function VendorOnboardingDashboardPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const { data } = useQuery({ queryKey: ['vendor-onboarding-dashboard'], queryFn: () => vendorDashboardApi.dashboard() });
  const recompute = useMutation({ mutationFn: () => vendorDashboardApi.recompute(), onSuccess: () => qc.invalidateQueries({ queryKey: ['vendor-onboarding-dashboard'] }) });
  const k = data?.kpis ?? {};
  const c = data?.charts ?? {};
  const exp = data?.tables.expiringDocuments;

  return (
    <>
      <PageHeader title="Vendor Onboarding Dashboard" subtitle="Applications, approvals, performance ranking and document compliance."
        action={hasPermission('vendor_onboarding.manage') && <Button startIcon={<Autorenew />} variant="outlined" onClick={() => recompute.mutate()} disabled={recompute.isPending}>Recompute Performance</Button>} />

      <Grid container spacing={2} mb={1}>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#1e40af" label="Total Applications" value={n(k.totalApplications)} icon={<Description />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#64748b" label="Draft" value={n(k.draft)} icon={<Description />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#7c3aed" label="Under Review" value={n(k.underReview)} icon={<PendingActions />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#0284c7" label="Pending Approval" value={n(k.pendingApproval)} icon={<PendingActions />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#16a34a" label="Active Vendors" value={n(k.activeVendors)} icon={<Verified />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard gradient color="#dc2626" label="Rejected" value={n(k.rejected)} icon={<Block />} /></Grid>
      </Grid>
      <Grid container spacing={2} mb={1}>
        <Grid item xs={6} sm={4} lg={2}><StatCard color="#16a34a" label="Approved" value={n(k.approvedVendors)} icon={<Verified />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard color="#64748b" label="Inactive" value={n(k.inactiveVendors)} icon={<Block />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard color="#d97706" label="Suspended" value={n(k.suspendedVendors)} icon={<Block />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard color="#d97706" label="On Hold" value={n(k.onHoldVendors)} icon={<Block />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard color="#dc2626" label="Blacklisted" value={n(k.blacklistedVendors)} icon={<Block />} /></Grid>
        <Grid item xs={6} sm={4} lg={2}><StatCard color="#dc2626" label="Docs Expired" value={n(exp?.expired.length)} hint={`${n(exp?.within30Days.length)} expiring ≤30d`} icon={<Description />} /></Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}><ChartCard title="Applications by Status" data={c.applicationsByStatus ?? c.vendorStatus ?? []} /></Grid>
        <Grid item xs={12} md={4}><ChartCard title="Vendors by Category" data={c.vendorsByCategory ?? c.vendorTypeDistribution ?? []} /></Grid>
        <Grid item xs={12} md={4}><ChartCard title="Vendors by State" data={c.vendorsByState ?? c.stateWiseVendors ?? []} /></Grid>
        <Grid item xs={12} md={4}><ChartCard title="Approval TAT" data={c.approvalTAT ?? []} /></Grid>
        <Grid item xs={12} md={4}><ChartCard title="Top Performing Vendors" data={c.topPerformingVendors ?? []} /></Grid>
        <Grid item xs={12} md={4}><ChartCard title="Approval Stage" data={c.approvalStage ?? []} /></Grid>
        <Grid item xs={12} md={4}><ChartCard title="Performance Ranking" data={c.performanceRanking ?? []} /></Grid>
        <Grid item xs={12} md={4}><ChartCard title="Workload (sites/vendor)" data={c.workloadDistribution ?? []} /></Grid>

        <Grid item xs={12} md={6}>
          <Card><CardHeader title="Recently Submitted" titleTypographyProps={{ variant: 'subtitle1' }} />
            <CardContent sx={{ p: 0, overflowX: 'auto' }}>
              {(data?.tables.recentlySubmitted.length ?? 0) === 0 ? <EmptyState title="None" dense /> : (
                <Table size="small"><TableHead><TableRow><TableCell>Application</TableCell><TableCell>Vendor</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
                  <TableBody>{data!.tables.recentlySubmitted.map((r, i) => <TableRow key={i}><TableCell>{String(r.applicationCode)}</TableCell><TableCell>{String(r.legalName)}</TableCell><TableCell><Chip size="small" label={String(r.status).replace(/_/g, ' ')} /></TableCell></TableRow>)}</TableBody></Table>
              )}
            </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card><CardHeader title="Pending Approvals" titleTypographyProps={{ variant: 'subtitle1' }} />
            <CardContent sx={{ p: 0, overflowX: 'auto' }}>
              {(data?.tables.pendingApprovals.length ?? 0) === 0 ? <EmptyState title="None" dense /> : (
                <Table size="small"><TableHead><TableRow><TableCell>Application</TableCell><TableCell>Vendor</TableCell><TableCell>Stage</TableCell></TableRow></TableHead>
                  <TableBody>{data!.tables.pendingApprovals.map((r, i) => <TableRow key={i}><TableCell>{String(r.applicationCode)}</TableCell><TableCell>{String(r.legalName)}</TableCell><TableCell>{String(r.stage).replace(/_/g, ' ')}</TableCell></TableRow>)}</TableBody></Table>
              )}
            </CardContent></Card>
        </Grid>
      </Grid>
    </>
  );
}
